/* eslint-disable no-warning-comments */
import { deepEqual, sleep } from '@deckee/utilities';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { callEndpoint, telemetry } from '../../../services/backend';
import { getLayerDefinitions } from '../../../services/map-layer';
import {
  hideActiveTripFromMap,
  removeActiveTripFromMap,
} from '../../../services/map-layer/line-layers/log-trips';
import { mapBaseLayerDefinitions } from '../../../services/map-style';
import { MAP_URL } from '../../../services/route/permissions';
import { ROUTE_MARKER_READ } from '../../../services/route/route-names';
import { transitionToNextRoute } from '../../../services/route/route-navigation';
import {
  clearMapLocationChange,
  closePopover,
  moveMapToLocation,
  setDropPinLocation,
  showPopover,
  updateCurrentMapParams as updateCurrentMapParamsAction,
  updateRouteLocationOnLoad as updateRouteLocationOnLoadAction,
  updateSelectedMarker,
  updateViewportSize,
} from '../../../services/store/modules/app-controls';
import {
  clearFollowModeActive,
  selectTripModeActive,
} from '../../../services/store/modules/log-trips';
import {
  getSelectedLayers,
  switchLayer,
} from '../../../services/store/modules/map-settings';
import { updateWeatherViewport } from '../../../services/store/modules/weather';
import { shiftLatLonByPixelRelativeToMapContainer } from '../../../services/utilities';
import Mapbox from './components/mapbox';
import {
  updateMarkersCacheWithGivenItems,
  updateShownMarkers,
} from './lib/marker-cache';
import styles from './MapContainer.scss';
import isMapVisible from '../../helpers/is-map-visible';

const cx = classNames.bind(styles);

class MapContainer extends React.Component {
  constructor(props) {
    super(props);

    this.BASE_MAP_LAYER_DEFINITIONS = mapBaseLayerDefinitions;
    this.layerDependencies = {};

    // Define initial state
    this.state = {
      markers: [],
      geoJsonToDisplay: null,
    };

    // TODO: Calling a function that should be abstracted through a prop.
    this.mapControlInitialize = (mapControl) => {
      this.mapControl = mapControl;
    };

    this.mapLocationChangeLocation = (newMapLocation) => {
      // Return and do nothing if no new map details supplied
      if (!Object.entries(newMapLocation).length > 0) return;

      const { center, zoom, bearing, pitch, offsetX, offsetY, jumpToLocation } =
        newMapLocation;

      // Only add the params if they have values
      const newMapLocationParams = {};
      if (center) {
        newMapLocationParams.center = [center.lon, center.lat];
      }
      if (zoom) {
        newMapLocationParams.zoom = zoom;
      }
      if (bearing || bearing === 0) {
        newMapLocationParams.bearing = bearing;
      }
      if (pitch || pitch === 0) {
        newMapLocationParams.pitch = pitch;
      }

      if (offsetX || offsetY) {
        newMapLocationParams.center = shiftLatLonByPixelRelativeToMapContainer(
          this.mapControl,
          center.lat,
          center.lon,
          offsetX || 0,
          offsetY || 0,
        );
      }

      // Add a delay here as updating the state can interfere with the maps location change
      // function on android. A 250ms delay doesn't detract from the UX too much.
      setTimeout(async () => {
        // TODO: Calling a function that should be abstracted through a prop.
        if (jumpToLocation) {
          await this.mapControl.jumpTo({
            ...newMapLocationParams,
          });
        } else {
          await this.mapControl.easeTo({
            ...newMapLocationParams,
          });
        }
      }, 250);
    };

    // TODO: REDUX >> fix viewport update
    this.onViewPortUpdated = (viewPortDetails) => {
      const {
        updateCurrentMapParams,
        weatherOnMapCenter,
        dispatchUpdateViewport,
      } = this.props;

      // Update the store with the current map params
      updateCurrentMapParams({ viewPortDetails, weatherOnMapCenter });

      this.updateMarkerCache();
      dispatchUpdateViewport();
    };

    this.updateMapState = (nextStateValues) => {
      this.setState({
        ...nextStateValues,
      });
    };
  }

  componentDidMount() {
    const { currentPosition, updateRouteLocationOnLoad, currentMapParams } =
      this.props;

    this.layerDependencies.redirectToMapMarker = (marker) => {
      transitionToNextRoute(ROUTE_MARKER_READ, {
        markerType: marker.markerType,
        id: marker.id,
      });
    };

    // this.layerDependencies.showPopOverForConfiguration = dispatchShowPopover;

    this.layerDependencies.softRedirectToMainMapRoute = () =>
      window.history.replaceState({}, '', MAP_URL);

    this.layerDependencies.getMapMarker = async (id) =>
      this.getMapMarkerAndAddToCache(id);

    // TODO: We should have a promise property passed to MapBox here and only take actions such as
    // update shown map makers etc once we know the bounds of the actual map. Previously we were
    // doing requests based on the default bounding box.
    if (
      currentMapParams &&
      currentMapParams.viewPortDetails &&
      currentMapParams.viewPortDetails.bounds
    ) {
      this.updateShowMapMarker();
    }

    updateRouteLocationOnLoad(currentPosition);

    this.updateMarkerCache();
  }

  componentDidUpdate(
    prevProps = {
      selectedMarkerIds: [],
    },
  ) {
    const {
      dispatchClearMapLocationChange,
      mapLocationChange,
      selectedMarkerIds,
      currentMapParams,
      selectedLayerTypes,
      location,
      activeTrip,
      activeTripHidden,
    } = this.props;

    if (activeTripHidden || activeTripHidden !== prevProps?.activeTripHidden) {
      hideActiveTripFromMap(activeTripHidden);
    }

    if (activeTrip !== prevProps?.activeTrip && !activeTrip) {
      removeActiveTripFromMap();
    }

    // Determine if map viewport bounds are available
    const prevViewportBounds =
      prevProps?.currentMapParams?.viewPortDetails?.bounds || null;
    const viewportBounds = currentMapParams?.viewPortDetails?.bounds || null;

    // /////////////////////////////////////////////////////////////////////////////////
    // ShowMarker - only call updateShowMapMarker if we have bounds
    if (viewportBounds && !prevViewportBounds) {
      this.updateShowMapMarker();
    }

    const selectedMarkerIdsChanged = !deepEqual(
      selectedMarkerIds,
      prevProps?.selectedMarkerIds,
    );
    if (viewportBounds && selectedMarkerIdsChanged) {
      this.updateShowMapMarker();
    }

    // Check for route change
    const prevLocationKey = prevProps?.location?.key || null;
    const locationKey = location?.key || null;
    if (locationKey !== prevLocationKey) {
      // This serves to add the 'zoom/lat/lon' hash to the url when changing routes
      if (this.mapControl) this.mapControl.easeTo({});
    }

    // Check for map options change
    if (selectedLayerTypes !== prevProps.selectedLayerTypes) {
      // This serves to refresh the map layers after toggling layers etc
      if (this.mapControl) this.mapControl.easeTo({});
    }

    // Check if there is a map location or view options change
    if (
      mapLocationChange?.constructor === Object &&
      Object.keys(mapLocationChange).length > 0
    ) {
      // Deep check the map options
      const mapOptionsHaveChanged = !deepEqual(
        prevProps?.mapLocationChange,
        mapLocationChange,
      );
      if (mapOptionsHaveChanged) {
        this.mapLocationChangeLocation(mapLocationChange);
      } else {
        dispatchClearMapLocationChange();
      }
    }
  }

  componentWillUnmount() {
    clearInterval(this.userLocationChecks);
  }

  // eslint-disable-next-line class-methods-use-this
  async getMapMarkerAndAddToCache(id) {
    // Attempt to get marker with exponential back-off.
    let marker;
    let delay = 100;
    do {
      /* eslint-disable-next-line no-await-in-loop */
      await sleep(delay);
      /* eslint-disable-next-line no-await-in-loop */
      marker = await callEndpoint(callEndpoint.endpoints.mapSearchQueryById, {
        id,
      });
      delay *= 2;
    } while (!marker && delay < 10000);

    if (!marker) {
      telemetry(telemetry.WARN, `Marker not found for id: ${id}`);
      return null;
    }
    if (marker && !marker.hidden) {
      await updateMarkersCacheWithGivenItems([marker]);
    }
    return marker;
  }

  async updateShowMapMarker() {
    const {
      selectedMarkerIds,
      selectedLayerTypes,
      currentMapParams,
      dispatch,
    } = this.props;

    try {
      // Return if no selected marker given
      if (
        !selectedMarkerIds ||
        !selectedMarkerIds.length ||
        !selectedMarkerIds[0]
      )
        return;

      const marker = await this.getMapMarkerAndAddToCache(selectedMarkerIds[0]);

      const shownMarkers = await updateShownMarkers({
        bounds: currentMapParams.viewPortDetails.bounds,
        selectedMarkerIds,
        selectedLayerTypes,
      });
      this.setState({
        markers: shownMarkers,
      });

      const layerDefinition = getLayerDefinitions()[marker.markerType];
      const configuration = {
        icon: layerDefinition.icon,
        ...layerDefinition.popOverConfiguration(marker),
      };

      // Turn on the layer if not currently enabled
      const selectedEntityType = configuration && configuration.markerType;
      const selectedLayerIsEnabled = selectedLayerTypes.find(
        (name) => name === selectedEntityType,
      );

      if (!selectedLayerIsEnabled) {
        dispatch(switchLayer({ name: selectedEntityType, enabled: true }));
      }
    } catch (error) {
      // TODO: this error is fired sometimes when submitting a new marker
      // >> as the request is made before it has finished creating the new marker entity
      telemetry(
        telemetry.ERROR,
        `[updateShowMapMarker] error parsing selectedMarkerIds of ${selectedMarkerIds} with error: ${error.message}`,
        error,
      );
    }
  }

  async updateMarkerCache(givenBounds, givenEntityTypes) {
    if (isMapVisible()) {
      const { selectedLayerTypes, selectedMarkerIds, currentMapParams } =
        this.props;

      const boundingBox =
        givenBounds || currentMapParams.viewPortDetails.bounds;
      if (!boundingBox) return;

      const layerDefinitions = getLayerDefinitions();

      const standardMarkerLayersSelected = Object.keys(layerDefinitions)
        .filter(
          (markerType) =>
            layerDefinitions[markerType].group === 'standardMarker' &&
            selectedLayerTypes.indexOf(markerType) !== -1,
        )
        .map((key) => layerDefinitions[key].standardMarkerOptions.markerType);

      const entityTypes = givenEntityTypes || standardMarkerLayersSelected;

      // Removing eventMarker from main map search
      const allowedEntityTypes = entityTypes.filter(
        (entity) => !['eventMarker', 'boatUsMarker'].includes(entity),
      );

      let results;
      if (entityTypes.length) {
        results = await callEndpoint(
          callEndpoint.endpoints.mapSearchQueryByBoundingBoxAndType,
          {
            mapMarkerTypes: [...allowedEntityTypes],
            boundingBox,
          },
        );
        // There is a bug with the `mapSearchQueryByBoundingBoxAndType` andpoint where it returns {data: {results: []}}
        // when there are no results. So just resetting the results array to empty if this situation occurs
        if (results.results?.length === 0) {
          results = [];
        }
      } else {
        results = [];
      }

      const uniqueMarkerImageIds = [
        ...new Set((results || []).map((i) => i.image)),
        ...new Set((results || []).map((i) => i.markerImage)),
      ];

      const loadMarkerImages = async (imageIds) => {
        // Add the marker images from the endpoint to the map if unique and not already loaded
        const promiseArray = imageIds.map(async (imageId) => {
          if (imageId && !this.mapControl.hasImage(imageId)) {
            this.mapControl.loadImage(
              `/public/app-main/image/pinWrapImage/${imageId}`,
              (error, image) => {
                if (error) throw error;
                this.mapControl.addImage(imageId, image);
              },
            );
          }
          return true;
        });
        await Promise.all(promiseArray);
      };

      await loadMarkerImages(uniqueMarkerImageIds);

      await updateMarkersCacheWithGivenItems(results);
      const markers = await updateShownMarkers({
        bounds: currentMapParams.viewPortDetails.bounds,
        selectedMarkerIds,
        selectedLayerTypes,
      });

      this.setState({
        markers,
      });
    }
  }

  render() {
    const {
      activeTrip,
      history,
      activeTripHidden,
      appViewportSize,
      currentHeading,
      currentMapboxBearing,
      currentPosition,
      currentPositionMarkerHidden,
      dispatchClearFollowModeActive,
      dispatchClosePopover,
      dispatchMoveMapToLocation,
      dispatchSetDropPinLocation,
      dispatchResetPitch,
      dispatchShowPopover,
      dispatchUpdateSelectedMarker,
      floatingPaneOffset,
      followMode,
      followModeActive,
      tripModeActive,
      hidingAllLayers,
      locationMarkerPosition,
      mapHolderOffset,
      selectedBaseMapLayer,
      selectedLayerTypes,
      selectedMarkerIds,
      showingDistanceTool,
      showingLocationMapMarker,
      is3dMapEnabled,
    } = this.props;

    const { markers, geoJsonToDisplay } = this.state;

    return (
      <div
        className={cx('map-container')}
        style={{
          minHeight:
            appViewportSize.height > 0
              ? `${appViewportSize.height}px`
              : '100vh',

          // This transform prevents the bottom map offset being applied to tablet+ sizes.
          // Eg. use of the floatingPaneOffset is with desktop 'north follow mode'.
          // We should be able to remove this in the future when we change the mapbox wrapper
          // or achieve a bit easier with styled components
          transform: `translateX(${mapHolderOffset})
            translateY(${
              floatingPaneOffset && appViewportSize.width < 800
                ? floatingPaneOffset
                : '0'
            })`,
        }}
      >
        <Mapbox
          className={cx('map-container__mapbox')}
          history={history}
          currentMapboxBearing={currentMapboxBearing}
          currentPositionMarkerHidden={currentPositionMarkerHidden}
          activeTrip={activeTrip}
          activeTripHidden={activeTripHidden}
          followMode={followMode}
          followModeActive={followModeActive}
          tripModeActive={tripModeActive}
          dispatchClosePopover={dispatchClosePopover}
          dispatchShowPopover={dispatchShowPopover}
          dispatchUpdateSelectedMarker={dispatchUpdateSelectedMarker}
          dispatchClearFollowModeActive={dispatchClearFollowModeActive}
          dispatchMoveMapToLocation={dispatchMoveMapToLocation}
          dispatchSetDropPinLocation={dispatchSetDropPinLocation}
          dispatchResetPitch={dispatchResetPitch}
          mapControlInitialize={this.mapControlInitialize}
          layerDependencies={this.layerDependencies}
          baseStyle={
            this.BASE_MAP_LAYER_DEFINITIONS[selectedBaseMapLayer].style
          }
          selectedLayerTypes={selectedLayerTypes}
          hidingAllLayers={hidingAllLayers}
          currentPosition={currentPosition}
          currentHeading={currentHeading}
          showingDistanceTool={showingDistanceTool}
          onViewPortUpdated={this.onViewPortUpdated}
          showingLocationMapMarker={showingLocationMapMarker}
          locationMarkerPosition={locationMarkerPosition}
          markers={markers}
          selectedMarkerIds={selectedMarkerIds}
          geoJsonToDisplay={geoJsonToDisplay}
          // `appViewportSize` is required to minimize the number of times we call resize.
          appViewportSize={appViewportSize}
          is3dMapEnabled={is3dMapEnabled}
        />
      </div>
    );
  }
}

// currentHeading:
// currentPosition:
MapContainer.propTypes = {
  currentMapboxBearing: PropTypes.number.isRequired,
  currentPositionMarkerHidden: PropTypes.bool.isRequired,
  activeTrip: PropTypes.bool.isRequired,
  activeTripHidden: PropTypes.bool.isRequired,
  followMode: PropTypes.string.isRequired,
  followModeActive: PropTypes.bool.isRequired,
  tripModeActive: PropTypes.bool.isRequired,

  dispatchClearMapLocationChange: PropTypes.func.isRequired,
  dispatchClearFollowModeActive: PropTypes.func.isRequired,
  dispatchClosePopover: PropTypes.func.isRequired,
  dispatchMoveMapToLocation: PropTypes.func.isRequired,
  dispatchResetPitch: PropTypes.func.isRequired,
  dispatchShowPopover: PropTypes.func.isRequired,
  dispatchUpdateSelectedMarker: PropTypes.func.isRequired,

  dispatchUpdateViewport: PropTypes.func.isRequired,

  appViewportSize: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }).isRequired,

  currentWeatherRequestLocation: PropTypes.shape({
    lat: PropTypes.number,
    lon: PropTypes.number,
  }).isRequired,
  selectedBaseMapLayer: PropTypes.string.isRequired,
  selectedLayerTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  hidingAllLayers: PropTypes.bool.isRequired,
  showingDistanceTool: PropTypes.bool.isRequired,
  showingLocationMapMarker: PropTypes.bool.isRequired,
  locationMarkerPosition: PropTypes.shape({
    lat: PropTypes.number,
    lon: PropTypes.number,
  }).isRequired,
  updateCurrentMapParams: PropTypes.func.isRequired,
  currentPosition: PropTypes.shape({
    lat: PropTypes.number,
    lon: PropTypes.number,
  }).isRequired,
  currentHeading: PropTypes.number,

  currentMapParams: PropTypes.shape({
    viewPortDetails: PropTypes.shape({
      center: PropTypes.shape({
        lat: PropTypes.number,
        lon: PropTypes.number,
      }),
      bounds: PropTypes.arrayOf(
        PropTypes.shape({
          lat: PropTypes.number,
          lon: PropTypes.number,
        }),
      ),
      zoom: PropTypes.number,
      bearing: PropTypes.number,
    }),
  }),
  mapLocationChange: PropTypes.shape({}),
  selectedMarkerIds: PropTypes.arrayOf(PropTypes.string),
  location: PropTypes.shape({
    key: PropTypes.string,
  }),
  updateRouteLocationOnLoad: PropTypes.func.isRequired,
  mapHolderOffset: PropTypes.string,
  floatingPaneOffset: PropTypes.string,
  is3dMapEnabled: PropTypes.bool.isRequired,
};

MapContainer.defaultProps = {
  location: {},
  currentMapParams: {
    viewPortDetails: {
      center:
        window.DECKEE_CONFIG && window.DECKEE_CONFIG.estimatedUserLocation
          ? window.DECKEE_CONFIG.estimatedUserLocation
          : { lat: -33.853999, lon: 151.215121 },
      bounds: null,
      zoom: 10,
      bearing: 0,
    },
  },
  currentHeading: null,

  mapLocationChange: undefined,
  selectedMarkerIds: [],
  mapHolderOffset: '0px',
  floatingPaneOffset: null,
};

const mapStateToProps = (state) => ({
  // mapped state.geoService
  currentHeading: state.geoService.currentHeading,
  currentPosition: state.geoService.currentPosition,

  // mapped state.logTrips
  activeTrip: state.logTrips.activeTrip,
  activeTripHidden: state.logTrips.activeTripHidden,
  currentPositionMarkerHidden: state.logTrips.currentPositionMarkerHidden,
  followMode: state.logTrips.followMode,
  followModeActive: state.logTrips.followModeActive,
  tripModeActive: selectTripModeActive(),

  // mapped state.appControls
  appViewportSize: state.appControls.appViewportSize,
  currentMapboxBearing:
    state.appControls.currentMapParams.viewPortDetails.bearing,
  currentMapParams: state.appControls.currentMapParams,
  floatingPaneOffset: state.appControls.floatingPaneOffset,
  hidingAllLayers: state.appControls.hidingAllLayers,
  locationMarkerPosition: state.appControls.locationMarkerPosition,
  mapHolderOffset: state.appControls.mapHolderOffset,
  mapLocationChange: state.appControls.mapLocationChange,
  newMarkerType: state.appControls.newMarkerType,
  selectedBaseMapLayer: state.appControls.selectedBaseMapLayer,
  selectedLayerTypes: getSelectedLayers(state),
  selectedMarkerIds: state.appControls.selectedMarkerIds,
  showingDistanceTool: state.appControls.showingDistanceTool,
  showingLocationMapMarker: state.appControls.showingLocationMapMarker,
  showingPageHolder: state.appControls.showingPageHolder,
  weatherOnMapCenter:
    state.appControls.memberSettings.weatherConfig.weatherOnMapCenter,
  weatherOnStartup:
    state.appControls.memberSettings.weatherConfig.weatherOnStartup,
  is3dMapEnabled: state.mapSettings.is3dEnabled,

  // mapped state.weather
  currentWeatherRequestLocation: state.weather.currentWeatherRequestLocation,
});

const mapDispatchToProps = (dispatch) => ({
  dispatch,
  dispatchUpdateViewport: () => {
    dispatch(updateViewportSize());
  },
  updateCurrentMapParams: (mapParams) => {
    dispatch(updateCurrentMapParamsAction(mapParams));
    dispatch(updateWeatherViewport(mapParams));
  },
  updateSelectedMarker: (selectedMarker) =>
    dispatch(updateSelectedMarker(selectedMarker)),
  updateRouteLocationOnLoad: (selectedMarker) =>
    dispatch(updateRouteLocationOnLoadAction(selectedMarker)),

  dispatchClearMapLocationChange: () => dispatch(clearMapLocationChange()),
  dispatchClosePopover: () => dispatch(closePopover()),
  dispatchShowPopover: (popoverConfig, heading) =>
    dispatch(showPopover(popoverConfig, heading)),
  dispatchUpdateSelectedMarker: (selectedMarker) =>
    dispatch(updateSelectedMarker(selectedMarker)),
  dispatchClearFollowModeActive: () => dispatch(clearFollowModeActive()),

  // TODO: Look into these further with addition of `react-map-gl` and potentially redux-thunk
  dispatchMoveMapToLocation: async (currentLocation) =>
    dispatch(await moveMapToLocation(currentLocation)),
  dispatchSetDropPinLocation: async (location) =>
    dispatch(await setDropPinLocation(location)),
  dispatchResetBearingAndPitch: async () =>
    dispatch(await moveMapToLocation({ bearing: 0, pitch: 0 })),
  dispatchResetPitch: async () =>
    dispatch(await moveMapToLocation({ pitch: 0 })),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(MapContainer));
